import type { LucideIcon } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { cn } from 'lib/utils'

export interface FieldAvatarProps {
	/** The picture. A missing or broken one falls through to the fallback. */
	src?: string | null
	/** Glyph for when there is no picture and no name to take initials from. */
	fallbackIcon: LucideIcon
	/**
	 * A person's name. Initials say more than a generic body glyph when several
	 * rows would otherwise carry the identical silhouette.
	 */
	name?: string | null
	/** What the picture is of, for a screen reader. */
	label?: string
	/** Size and colour overrides. Defaults to the 44px row avatar. */
	className?: string
	/** Tints the fallback glyph. The initials keep their own colour. */
	iconClassName?: string
}

const initialsOf = (name: string): string => {
	const parts = name.trim().split(/\s+/).filter(Boolean)
	if (parts.length === 0) return ''
	if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
	return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase()
}

/**
 * The round picture on the left of a field row.
 *
 * Round, and the same size, everywhere in the field app. A location, a project
 * and a person are different kinds of thing, but on a phone they are all "the
 * row I am looking for", and a rep recognises the photograph before they read
 * the name. Squares and circles mixed down one list makes the eye work for
 * nothing.
 *
 * Built on the ShadCN `Avatar` rather than a bare `<img>` because it already
 * handles the case that matters most here: a `src` that 404s. Cloudinary URLs
 * outlive the records that point at them, and a broken image icon in a list is
 * worse than the glyph it replaced.
 *
 * The fallback is always rendered underneath, so a slow image never leaves a
 * hole in the row.
 */
export function FieldAvatar({
	src,
	fallbackIcon: Fallback,
	name,
	label,
	className,
	iconClassName,
}: FieldAvatarProps) {
	const initials = name ? initialsOf(name) : ''

	return (
		/*
			The NAME lives on the wrapper, and both children are hidden from the
			accessibility tree. The fallback is always rendered underneath the
			image, so labelling them separately made every row announce itself
			twice; and labelling only the image would leave the glyph nameless
			whenever a picture 404s. One node, whatever is actually on screen.
		*/
		<Avatar
			role="img"
			aria-label={label}
			className={cn('h-11 w-11 border border-gray-200 bg-gray-50', className)}
		>
			<AvatarImage src={src || undefined} alt="" aria-hidden="true" loading="lazy" />
			<AvatarFallback className="bg-transparent" aria-hidden="true">
				{initials ? (
					<span className="text-sm font-semibold text-gray-500">{initials}</span>
				) : (
					<Fallback className={cn('h-5 w-5 text-gray-400', iconClassName)} />
				)}
			</AvatarFallback>
		</Avatar>
	)
}

export default FieldAvatar
